import { Component, HostListener, Inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { AddExternalPaymentMethodDialogComponent } from '../add-external-payment-method-dialog/add-external-payment-method-dialog.component';
import { SharedModule } from '../../shared.module';
import { BusinessService } from '../../../_core/state/business';
import { SessionQuery } from '../../../_core/state/session';
import { GlobalQuery } from '../../../_core/state/global';

export interface AddTemplateDialogDataModel {
	name: string;
	creativePackageBusinessIds: string[];
}

@Component({
	selector: 'app-add-template-dialog',
	standalone: true,
	imports: [MatButtonModule, MatDialogModule, ReactiveFormsModule, SharedModule],
	templateUrl: './add-template-dialog.component.html',
	styleUrl: './add-template-dialog.component.scss'
})
export class AddTemplateDialogComponent {
    public form: FormGroup;

    public businessOptions: { value: string, label: string }[] = [];
    public siteCopy$ = this.globalQuery.copy$;

    constructor(
        public dialogRef: MatDialogRef<AddExternalPaymentMethodDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private readonly formBuilder: FormBuilder,
        private readonly businessService: BusinessService,
        public sessionQuery: SessionQuery,
        private readonly globalQuery: GlobalQuery
    ) {
        this.businessService.findBusinessesForPackage().subscribe(data => {
            this.businessOptions = data;
        })

		this.form = this.formBuilder.group({
			name: ['', Validators.required],
			creativePackageBusinessIds: [[]]
		});

		this.form.get('creativePackageBusinessIds')!.valueChanges.subscribe(selected => {
			this.handleBusinessSelection(selected);
		});
	}

	submit() {
		const packageData = this.form.value;

		if (packageData.creativePackageBusinessIds && packageData.creativePackageBusinessIds.includes('ALL')) {
			// if ALL is selected, remove it from the payload because we don't want connections
			delete packageData.creativePackageBusinessIds;
		} else if (packageData.creativePackageBusinessIds) {
			packageData.businesses = packageData.creativePackageBusinessIds.map((id: string) => {
				return {
					id,
					name: this.businessOptions.find((option: any) => option.value === id).label
				};
			});
			delete packageData.creativePackageBusinessIds;
		}

		this.dialogRef.close(packageData);
	}

	private handleBusinessSelection(selected: string[]) {
		let hasAll = selected.includes('ALL');
		selected = selected.filter(value => value !== 'ALL');
		const allSelected = this.businessOptions.length > 0 && selected.length === this.businessOptions.length;

		// Only one business option available and it's selected - keep the selection
		if (this.businessOptions.length === 1 && selected.length === 1) {
			this.form.get('creativePackageBusinessIds')!.setValue([...selected], { emitEvent: false });
			return;
		}

		// only All is selected, select all
		if (hasAll && selected.length == 0) {
			this.form
				.get('creativePackageBusinessIds')!
				.setValue(['ALL', ...this.businessOptions.map((option: any) => option.value)], { emitEvent: false });
			return;
		}

		// remove "all" and select everything else - triggers when all are selected and one option is deselected
		if (hasAll && !allSelected && selected.length > 0) {
			this.form
				.get('creativePackageBusinessIds')!
				.setValue(['ALL', ...this.businessOptions.map((option: any) => option.value)], { emitEvent: false });
			return;
		}

		// if "all" isn't selected, but all options are -> select every option including "all"
		if (!hasAll && allSelected) {
			this.form.get('creativePackageBusinessIds')!.setValue(['ALL', ...selected], { emitEvent: false });
			return;
		}

		// Some businesses are unselected, remove ALL
		if (hasAll && !allSelected) {
			this.form.get('creativePackageBusinessIds')!.setValue([...selected], { emitEvent: false });
			return;
		}

		// Keep the current selection if it's not an edge case
		if (!allSelected) {
			this.form.get('creativePackageBusinessIds')!.setValue([...selected], { emitEvent: false });
			return;
		}
	}

	/** Force business id selection if the user is not a super admin */
	isValid(): boolean {
		return (
			this.form.valid && (this.form.get('creativePackageBusinessIds')!.value.length > 0 || this.sessionQuery.isAtLeastSuperAdmin())
		);
	}

	
	
	@HostListener('keyup.enter', ['$event'])
	onEnterKey(event: KeyboardEvent) {
		if (this.isValid()) {
			this.submit();
		}		
		event.preventDefault();
	}
}
